// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Physical quantities as the twin publishes them, in one place.
 *
 * The twin is inconsistent about how a rating or a reading is encoded. A
 * captured GA-10 read publishes `ratedPower: { value: 3000, unit: 'KiloVA' }`;
 * the spec's authored examples publish `ratedCurrent: '2000 A'`; a bare number
 * turns up too. Every consumer that compared or displayed one of these had its
 * own partial parser, and `String(rating)` on the structured form printed
 * `[object Object]` under a symbol. This module is the parser they share.
 *
 * Units are normalised to a base (V, A, W, VA, VAR, Hz, %, °F) with a scale, so
 * `12.47 KiloV` and `12470 V` compare equal and a kW reading can be checked
 * against a kVA rating without silently dividing one by the other — that last
 * case is a rule in `powerFlowLive.ts`, and it needs to know which family a
 * number is in.
 */

export type UnitFamily = 'V' | 'A' | 'W' | 'VA' | 'VAR' | 'Hz' | '%' | 'degF' | 'degC' | 'unknown';

export interface Quantity {
  /** In the base unit of its family: volts, amps, watts, volt-amps… */
  base: number;
  family: UnitFamily;
  /** The unit as published, trimmed, for display when we do not reformat. */
  unit: string;
}

/** SI prefix multipliers for the spellings the twin uses. */
const PREFIX: Record<string, number> = {
  '': 1,
  k: 1e3,
  kilo: 1e3,
  m: 1e6,
  mega: 1e6,
  g: 1e9,
  giga: 1e9,
};

/** Family symbols, by their lower-cased spelling. */
const FAMILY: Record<string, UnitFamily> = {
  v: 'V',
  volt: 'V',
  volts: 'V',
  a: 'A',
  amp: 'A',
  amps: 'A',
  ampere: 'A',
  w: 'W',
  watt: 'W',
  watts: 'W',
  va: 'VA',
  var: 'VAR',
  hz: 'Hz',
  hertz: 'Hz',
  '%': '%',
  percent: '%',
  percent_rh: '%',
  deg_f: 'degF',
  '°f': 'degF',
  degf: 'degF',
  deg_c: 'degC',
  '°c': 'degC',
  degc: 'degC',
};

const UNIT_RE = /^(kilo|mega|giga|k|m|g)?(.+)$/i;

/** `'KiloVA'` → `{ scale: 1e3, family: 'VA' }`; unknown spellings → family `unknown`. */
export const parseUnit = (raw: string | undefined): { scale: number; family: UnitFamily } => {
  const text = (raw ?? '').trim();
  if (!text) return { scale: 1, family: 'unknown' };
  const direct = FAMILY[text.toLowerCase()];
  if (direct) return { scale: 1, family: direct };
  const match = text.match(UNIT_RE);
  if (!match) return { scale: 1, family: 'unknown' };
  const [, prefix = '', rest] = match;
  const family = FAMILY[rest.toLowerCase()];
  if (!family) return { scale: 1, family: 'unknown' };
  return { scale: PREFIX[prefix.toLowerCase()] ?? 1, family };
};

/**
 * Parse whatever the twin put in an attribute or a value envelope.
 *
 * Accepts `{ value, unit }`, a bare number (family unknown), or a string like
 * `'2000 A'` / `'480V'`. Returns undefined for anything else, including a
 * non-finite value — a rating of `NaN` must not survive into a percentage.
 */
export const parseQuantity = (raw: unknown): Quantity | undefined => {
  if (raw === null || raw === undefined) return undefined;

  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? { base: raw, family: 'unknown', unit: '' } : undefined;
  }

  if (typeof raw === 'string') {
    const match = raw.trim().match(/^(-?\d+(?:[.,]\d+)?)\s*([^\d\s].*)?$/);
    if (!match) return undefined;
    const value = Number(match[1].replace(',', '.'));
    if (!Number.isFinite(value)) return undefined;
    const unit = (match[2] ?? '').trim();
    const { scale, family } = parseUnit(unit);
    return { base: value * scale, family, unit };
  }

  if (typeof raw === 'object' && 'value' in (raw as Record<string, unknown>)) {
    const envelope = raw as { value: unknown; unit?: unknown };
    const published = envelope.value;
    if (typeof published !== 'number' && typeof published !== 'string') return undefined;
    const value =
      typeof published === 'number' ? published : Number(published.trim().replace(',', '.'));
    if (!Number.isFinite(value) || (typeof published === 'string' && !published.trim())) {
      return undefined;
    }
    const unit = typeof envelope.unit === 'string' ? envelope.unit.trim() : '';
    const { scale, family } = parseUnit(unit);
    return { base: value * scale, family, unit };
  }

  return undefined;
};

/** Display symbol per family at the given scale. */
const SYMBOL: Record<Exclude<UnitFamily, 'unknown'>, string> = {
  V: 'V',
  A: 'A',
  W: 'W',
  VA: 'VA',
  VAR: 'VAR',
  Hz: 'Hz',
  '%': '%',
  degF: '°F',
  degC: '°C',
};

const formatNumber = (value: number, maxFractionDigits: number): string =>
  value.toLocaleString('en-US', { maximumFractionDigits: maxFractionDigits });

/**
 * Format a quantity the way a nameplate reads it: `3,000 kVA`, `12.47 kV`,
 * `480 V`, `1,200 A`. Kilo is chosen for anything at or above 1,000 in the
 * V/W/VA/VAR/A families; percentages and temperatures print as they are.
 */
export const formatQuantity = (quantity: Quantity): string => {
  const { base, family } = quantity;
  if (family === 'unknown') {
    return quantity.unit ? `${formatNumber(base, 2)} ${quantity.unit}` : formatNumber(base, 2);
  }
  if (family === '%' || family === 'Hz' || family === 'degF' || family === 'degC') {
    const symbol = SYMBOL[family];
    return family === '%' ? `${formatNumber(base, 1)} %` : `${formatNumber(base, 2)} ${symbol}`;
  }
  const symbol = SYMBOL[family];
  if (family === 'A') return `${formatNumber(base, 0)} ${symbol}`;
  if (family === 'V') {
    return Math.abs(base) >= 1e3
      ? `${formatNumber(base / 1e3, 2)} k${symbol}`
      : `${formatNumber(base, 0)} ${symbol}`;
  }
  if (Math.abs(base) >= 1e3) return `${formatNumber(base / 1e3, 1)} k${symbol}`;
  return `${formatNumber(base, 1)} ${symbol}`;
};

/** A published attribute as a nameplate string, or undefined when unparseable. */
export const formatAttributeQuantity = (raw: unknown): string | undefined => {
  const quantity = parseQuantity(raw);
  if (!quantity) {
    if (typeof raw === 'string' && raw.trim().length > 0) return raw.trim();
    return undefined;
  }
  return formatQuantity(quantity);
};

/** Kilowatts (or kVA) as a live figure: `529 kW`, `1,488 kW`, `12.76 MW`. */
export const formatPowerKw = (kw: number, family: 'W' | 'VA' = 'W'): string => {
  const symbol = family === 'W' ? 'W' : 'VA';
  if (Math.abs(kw) >= 10_000) return `${formatNumber(kw / 1000, 2)} M${symbol}`;
  return `${formatNumber(kw, 0)} k${symbol}`;
};
